'use client';

import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import config from '@/config';

type Plan = {
  name: string;
  description?: string;
  isFeatured?: boolean;
  price: number;
  period: string;
  priceNote?: string;
  trialNote?: string;
  features: string[];
};

const plans: Plan[] = [
  {
    name: 'Free',
    description: 'Perfect to get started',
    price: 0,
    period: 'forever',
    features: [
      'Unlimited quizzes',
      '30 players',
      'Text-only questions',
      '10 question types',
      'Class results overview after every game',
    ],
  },
  {
    name: 'Pro',
    description: 'For active educators',
    isFeatured: true,
    price: 59,
    period: 'year',
    priceNote: 'about $4.92 a month',
    features: [
      'Everything in Free',
      '100 players',
      'Images, video & audio questions',
      'Random questions - pull from your question bank',
      'AI question generation',
      'Import quizzes from PDFs',
      'Design your own custom maps',
      'Run multiple games at once',
      'Practice mode for self-paced study',
      'Individual student results & exports',
      'Question Bank',
      'Custom avatar packs',
      'Private quizzes',
      'Duplicate quizzes in one click',
    ],
  },
];

export default function Pricing() {
  return (
    <section className="bg-paper-100 paper-grain" id="pricing">
      <div className="py-20 lg:py-28 px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="text-center max-w-4xl mx-auto mb-14">
          <p className="font-hand text-brand-600 text-2xl mb-3 -rotate-1 inline-block">
            no surprises
          </p>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink-900 leading-[1.02] mb-5">
            <span className="italic font-normal">Simple,</span> transparent
            pricing.
          </h2>
          <p className="font-display text-lg text-ink-600">
            One yearly plan unlocks everything. Start free, no card needed.
          </p>
        </div>

        <div className="relative flex justify-center flex-col lg:flex-row items-center lg:items-stretch gap-6 lg:gap-8 max-w-3xl mx-auto">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative w-full max-w-sm ${
                plan.isFeatured ? 'lg:-mt-4 lg:-mb-4' : ''
              }`}
            >
              {plan.isFeatured && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                  <span className="bg-gradient-to-r from-brand-500 to-brand-600 text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg shadow-brand-500/20">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div
                className={`relative flex flex-col h-full gap-6 z-10 bg-paper-50 p-8 rounded-sm transition-all ${
                  plan.isFeatured
                    ? 'ring-2 ring-brand-500 shadow-xl shadow-ink-900/10'
                    : 'border border-ink-900/15 shadow-sm hover:shadow-md'
                }`}
              >
                <div>
                  <p className="font-display font-bold text-2xl text-ink-900">
                    {plan.name}
                  </p>
                  {plan.description && (
                    <p className="font-display italic text-sm text-ink-400 mt-1">
                      {plan.description}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-end gap-1">
                    <p
                      className={`font-display tracking-tight font-black text-ink-900 ${
                        plan.isFeatured ? 'relative text-7xl' : 'text-6xl'
                      }`}
                    >
                      ${plan.price}
                      {plan.isFeatured && (
                        <svg
                          className="absolute -bottom-2 left-0 w-full text-brand-500"
                          viewBox="0 0 120 12"
                          fill="none"
                          preserveAspectRatio="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M2 8C28 3 52 3 78 6c14 1.5 28 2.5 40-1"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </p>
                    <span className="text-sm text-ink-400 mb-3">
                      /{plan.period}
                    </span>
                  </div>
                  {plan.priceNote ? (
                    <span className="font-hand text-brand-600 text-lg -rotate-1">
                      {plan.priceNote}
                    </span>
                  ) : (
                    <span className="font-display italic text-sm text-ink-400">
                      free, no card needed
                    </span>
                  )}
                  {plan.trialNote && (
                    <span className="font-display italic text-sm text-ink-400">
                      {plan.trialNote}
                    </span>
                  )}
                </div>

                {plan.features && (
                  <ul className="space-y-3 flex-1">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" />
                        <span className="text-ink-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <a
                  href={`${config.appUrl}/register`}
                  className={`btn rounded-full w-full shrink-button ${
                    plan.isFeatured
                      ? 'btn-primary text-white shadow-lg shadow-brand-500/20'
                      : 'btn-outline border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper-50 hover:border-ink-900'
                  }`}
                >
                  {plan.price > 0 ? 'Upgrade to Pro' : 'Get started'}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
